import { useState, useRef, useId } from "react";
import { cn } from "../lib/utils";

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  max?: number;
  className?: string;
}

export function TagInput({
  value,
  onChange,
  placeholder = "Add tag…",
  disabled,
  error,
  max,
  className,
}: TagInputProps) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag || value.includes(tag)) return;
    if (max !== undefined && value.length >= max) return;
    onChange([...value, tag]);
    setInput("");
  };

  const removeTag = (tag: string) => onChange(value.filter(t => t !== tag));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && input === "" && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  return (
    <div
      className={cn(
        "tag-input-wrap",
        error && "input-error",
        disabled && "tag-input-disabled",
        className
      )}
      onClick={() => inputRef.current?.focus()}
      role="group"
      aria-labelledby={id}
    >
      {value.map(tag => (
        <span key={tag} className="tag">
          {tag}
          {!disabled && (
            <button
              className="tag-remove"
              onClick={e => { e.stopPropagation(); removeTag(tag); }}
              aria-label={`Remove ${tag}`}
              tabIndex={-1}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </span>
      ))}
      {(!max || value.length < max) && (
        <input
          ref={inputRef}
          id={id}
          className="tag-input-field"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => addTag(input)}
          placeholder={value.length === 0 ? placeholder : ""}
          disabled={disabled}
          aria-label="New tag"
        />
      )}
    </div>
  );
}
